import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ActionStatus, ClientAction, Opportunity } from '../client-actions.model';

export type ClientActionDialogResult =
  | { action: 'save'; patch: Partial<ClientAction> }
  | { action: 'approve' }
  | { action: 'withdraw' };

interface DialogData {
  action: ClientAction;
  opportunities: Opportunity[];
}

const STATUS_OPTIONS: { label: string; value: ActionStatus }[] = [
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

@Component({
  selector: 'bai-client-action-details-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule, TagModule],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <!-- Origin badge -->
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <p-tag
          [value]="ca.sourceType === 'aiAgent' ? 'AI Agent' : 'Human'"
          [severity]="ca.sourceType === 'aiAgent' ? 'info' : 'secondary'"
          styleClass="text-xs"
        />
        <span>Created {{ ca.createdDate }}</span>
      </div>

      <!-- Action description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="cad-action">
          Action <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="cad-action"
          pInputText
          [ngModel]="form().action"
          (ngModelChange)="patch('action', $event)"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().action) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <!-- FA Name -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="cad-fa">Financial Assistant</label>
        <input
          id="cad-fa"
          pInputText
          [ngModel]="form().financialAssistantName"
          (ngModelChange)="patch('financialAssistantName', $event)"
          class="w-full"
        />
      </div>

      <!-- Source -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="cad-source">Source</label>
        <input
          id="cad-source"
          pInputText
          [ngModel]="form().source"
          (ngModelChange)="patch('source', $event)"
          class="w-full"
        />
      </div>

      <!-- Linked Opportunity -->
      @if (opportunityOptions.length > 0) {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="cad-opp">Linked Opportunity</label>
          <p-select
            inputId="cad-opp"
            [options]="opportunityOptions"
            [ngModel]="form().opportunityId"
            (ngModelChange)="patch('opportunityId', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="None"
            styleClass="w-full"
            [showClear]="true"
          />
        </div>
      }

      <!-- Dates + Status -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="cad-planned">Planned Date</label>
          <input
            id="cad-planned"
            pInputText
            type="date"
            [ngModel]="form().plannedDate"
            (ngModelChange)="patch('plannedDate', $event)"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="cad-deadline">Deadline</label>
          <input
            id="cad-deadline"
            pInputText
            type="date"
            [ngModel]="form().deadline"
            (ngModelChange)="patch('deadline', $event)"
            class="w-full"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="cad-status">Status</label>
        <p-select
          inputId="cad-status"
          [options]="statusOptions"
          [ngModel]="form().status"
          (ngModelChange)="patch('status', $event)"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
        />
      </div>

      <!-- Confirm / Withdraw -->
      @if (showConfirmActions()) {
        <div class="flex gap-2 pt-1 border-t border-gray-100">
          <p-button
            label="Approve"
            icon="pi pi-check"
            severity="success"
            [outlined]="true"
            size="small"
            (onClick)="approve()"
            aria-label="Approve action"
          />
          <p-button
            label="Withdraw"
            icon="pi pi-ban"
            severity="danger"
            [outlined]="true"
            size="small"
            (onClick)="withdraw()"
            aria-label="Withdraw action"
          />
        </div>
      }

      <!-- Footer -->
      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="cancel()" />
        <p-button label="Save" (onClick)="save()" />
      </div>

    </div>
  `,
})
export class ClientActionDetailsDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);

  readonly ca: ClientAction;
  readonly opportunityOptions: { label: string; value: string }[];
  readonly statusOptions = STATUS_OPTIONS;
  readonly submitted = signal(false);

  readonly form: ReturnType<typeof signal<{
    action: string;
    financialAssistantName: string;
    source: string;
    opportunityId: string | null;
    plannedDate: string;
    deadline: string;
    status: ActionStatus;
  }>>;

  readonly showConfirmActions = computed(
    () =>
      this.ca.sourceType === 'aiAgent' &&
      this.ca.status !== 'Withdrawn' &&
      this.ca.status !== 'Done'
  );

  constructor() {
    const data = this._config.data as DialogData;
    this.ca = data.action;
    this.opportunityOptions = (data.opportunities ?? []).map((o) => ({
      label: o.opportunityDescription,
      value: o.id,
    }));
    this.form = signal({
      action: this.ca.action,
      financialAssistantName: this.ca.financialAssistantName,
      source: this.ca.source,
      opportunityId: this.ca.opportunityId,
      plannedDate: this.ca.plannedDate,
      deadline: this.ca.deadline,
      status: this.ca.status,
    });
  }

  patch<K extends keyof ReturnType<typeof this.form>>(key: K, value: ReturnType<typeof this.form>[K]): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  save(): void {
    this.submitted.set(true);
    if (!this.form().action) return;
    this._ref.close({ action: 'save', patch: { ...this.form() } } satisfies ClientActionDialogResult);
  }

  approve(): void {
    this._ref.close({ action: 'approve' } satisfies ClientActionDialogResult);
  }

  withdraw(): void {
    this._ref.close({ action: 'withdraw' } satisfies ClientActionDialogResult);
  }

  cancel(): void {
    this._ref.close(null);
  }
}
